import React, { FC, RefObject, useMemo } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import useScrollToView from '../../../hooks/useScrollToView';
import { useAppSelector } from '../../../redux/hooks';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ReactComponent as SegmentIcon } from '../../../assets/segment.svg';
import { ReactComponent as TruckIcon } from '../../../assets/truck.svg';
import { ReactComponent as OrderIcon } from '../../../assets/icons/orders.svg';
import { MenuItem } from '../../EditDuplicateOrder/EditDuplicateOrder';
import { EntityType } from '../../../redux/Global.model';
import useAuth from '../../../providers/Auth/Auth.provider';

interface QuickMenuItemsProps {
  isOrderInfoVisible: boolean;
  isRouteInfoVisible: boolean;
  isDispatchInfoVisible: boolean;
  isAttachmentsVisible: boolean;
  tripsVisibilities: boolean[];
  orderInfoRef: RefObject<HTMLDivElement> | null;
  routeInfoRef: RefObject<HTMLDivElement> | null;
  dispatchRef: RefObject<HTMLDivElement> | null;
  attachmentsRef: RefObject<HTMLDivElement> | null;
  tripsRef: any;
  entity: EntityType;
}

const QuickMenuItems: FC<QuickMenuItemsProps> = ({
  isOrderInfoVisible,
  isRouteInfoVisible,
  isDispatchInfoVisible,
  isAttachmentsVisible,
  tripsVisibilities,
  orderInfoRef,
  routeInfoRef,
  dispatchRef,
  attachmentsRef,
  tripsRef,
  entity
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const segments = useAppSelector((state) => state.orders.trips);
  const [scrollToView] = useScrollToView();

  const menuItems = useMemo<MenuItem[]>(
    () => [
      {
        id: 1,
        text: 'Order Info',
        visible: isOrderInfoVisible,
        ref: orderInfoRef,
        icon: <OrderIcon className="create-load__menu__item__icon" />
      },
      {
        id: 2,
        text: 'Route',
        visible: isRouteInfoVisible,
        ref: routeInfoRef,
        icon: <LocationOnIcon className="create-load__menu__item__icon" />
      },
      ...(segments.length > 1
        ? segments?.map((_, index) => ({
            id: index + 3,
            text: `Trip ${index + 1} of ${segments.length}`,
            visible: tripsVisibilities[index],
            ref: tripsRef.current[index],
            icon: (
              <SegmentIcon
                className="create-load__menu__item__icon create-load__menu__item__segment_icon"
                fill={tripsVisibilities[index] ? '#3a4de9' : '#7e858e'}
              />
            )
          }))
        : []),
      ...(enableLtl && entity === EntityType.ORDER
        ? []
        : segments.length === 1
        ? [
            {
              id: 3,
              text: 'Dispatch',
              visible: isDispatchInfoVisible,
              ref: dispatchRef,
              icon: <TruckIcon className="create-load__menu__item__icon" />
            }
          ]
        : []),
      {
        id: segments.length > 1 ? segments.length + 3 : 4,
        text: 'Attachments',
        visible: isAttachmentsVisible,
        ref: attachmentsRef,
        icon: <AttachFileIcon className="create-load__menu__item__icon" />
      }
    ],
    [
      isRouteInfoVisible,
      isOrderInfoVisible,
      isDispatchInfoVisible,
      isAttachmentsVisible,
      segments.length,
      tripsVisibilities
    ]
  );

  const activeMenuItemId = useMemo<number>(
    () => menuItems.find((mi) => mi.visible)?.id || 1,
    [menuItems]
  );

  return (
    <Grid item lg={3} xl={3}>
      <Box className="create-load__menu-wrapper">
        <Box className="create-load__menu">
          {menuItems.map((item) => (
            <Box
              key={item.id}
              className={`create-load__menu__item ${
                item.id === activeMenuItemId && 'create-load__menu__item--active'
              }`}
              onClick={(): void => scrollToView(item.ref)}
            >
              {item.icon}
              <Box component="span">{item.text}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Grid>
  );
};

export default QuickMenuItems;
